import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../../../utils/exportColors';
import { jsPDF } from 'jspdf';
import { downloadBlob } from '../../toolPageUtils';

const EXPORT_SCALE = 2;
const A4 = { widthMm: 210, heightMm: 297, marginMm: 5 };

/**
 * @param {HTMLElement} root
 */
function fixExportRendering(root) {
    root.querySelectorAll('[data-export-only]').forEach((el) => {
        el.style.position = 'static';
        el.style.left = 'auto';
        el.style.top = 'auto';
        el.style.visibility = 'visible';
        el.style.width = '100%';
        el.style.height = 'auto';
        el.style.overflow = 'visible';
        el.style.clip = 'auto';
        el.style.clipPath = 'none';
        el.style.opacity = '1';
        el.style.pointerEvents = 'none';
    });
}

/**
 * @param {HTMLElement} element
 */
function cloneForExport(element) {
    const width = element.offsetWidth || element.scrollWidth;

    const clone = element.cloneNode(true);
    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = [
        'position:fixed',
        'left:-20000px',
        'top:0',
        `width:${width}px`,
        'overflow:visible',
        'pointer-events:none',
        'z-index:-1',
        'background:#ffffff',
    ].join(';');

    clone.style.width = `${width}px`;
    clone.style.maxWidth = `${width}px`;
    clone.style.transform = 'none';
    clone.style.overflow = 'visible';
    clone.style.background = '#ffffff';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    fixExportRendering(clone);

    const height = Math.max(clone.scrollHeight, clone.offsetHeight, element.scrollHeight) + 4;
    wrapper.style.height = `${height}px`;
    return { clone, wrapper, width, height };
}

/**
 * @param {HTMLElement} element
 */
export async function captureBmiCalculatorCanvas(element) {
    const { clone, wrapper, width, height } = cloneForExport(element);

    try {
        await document.fonts.ready;
        return await html2canvas(clone, {
            scale: EXPORT_SCALE,
            width,
            height,
            windowWidth: width,
            windowHeight: height,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#ffffff',
            onclone: (doc) => {
                const root = doc.querySelector('[data-bmi-calc-export]');
                if (root instanceof HTMLElement) { fixExportRendering(root); sanitizeExportColors(root); }
            },
        });
    } finally {
        wrapper.remove();
    }
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {import('jspdf').jsPDF} pdf
 */
function addCanvasToSingleA4Page(canvas, pdf) {
    const margin = A4.marginMm;
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pdfWidth - margin * 2;
    const contentHeight = pdfHeight - margin * 2;
    const scale = Math.min(contentWidth / canvas.width, contentHeight / canvas.height);
    const drawWidth = canvas.width * scale;
    const drawHeight = canvas.height * scale;
    const offsetX = margin + (contentWidth - drawWidth) / 2;
    const offsetY = margin + (contentHeight - drawHeight) / 2;

    pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        offsetX,
        offsetY,
        drawWidth,
        drawHeight,
        undefined,
        'FAST',
    );
}

/**
 * @param {HTMLElement} element
 * @param {string} filename
 */
export async function downloadBmiCalculatorPdf(element, filename = 'bmi-calculator.pdf') {
    const canvas = await captureBmiCalculatorCanvas(element);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    addCanvasToSingleA4Page(canvas, pdf);
    downloadBlob(pdf.output('blob'), filename);
}
